// ?debug flicker probe: measures the brightness of every drawn frame and records short dark dips
// (a frame or few clearly darker than the ones around it), with what was going on at the time.
// A dip it records is in our pixels; a flicker the viewer sees that it does not record happened
// after the page drew the frame (browser, compositor or driver).

/** A dip must be at least this much darker than the frames before it. */
export const DIP_DROP = 0.07;
/** ...and last at most this many frames before the picture is back. */
export const DIP_MAX_FRAMES = 3;
const BASELINE_FRAMES = 5;

export interface Dip { frames: number; depth: number; baseline: number; lum: number }

/** Finds dips in a stream of frame brightness values. Pure. */
export class DipDetector {
  private lums: number[] = [];

  /** Feed one frame's mean brightness (0..1). Returns a dip that just ended with this frame, or null. */
  push(lum: number): Dip | null {
    const h = this.lums;
    h.push(lum);
    if (h.length > BASELINE_FRAMES + DIP_MAX_FRAMES + 1) h.shift();
    for (let k = 1; k <= DIP_MAX_FRAMES; k++) {
      const before = h.slice(-(k + 1 + BASELINE_FRAMES), -(k + 1));
      if (before.length < BASELINE_FRAMES) continue;
      const baseline = median(before);
      if (baseline < 0.02) continue; // nothing to darken
      const run = h.slice(-(k + 1), -1);
      const darkest = Math.min(...run);
      const dark = run.every((v) => v < baseline * (1 - DIP_DROP));
      const back = lum >= baseline * (1 - DIP_DROP / 2);
      // the frame just before the run must not already be dark, or this is the tail of a longer run
      const edge = h[h.length - k - 2];
      if (dark && back && edge !== undefined && edge >= baseline * (1 - DIP_DROP / 2)) {
        this.lums = [lum];
        return { frames: k, depth: 1 - darkest / baseline, baseline, lum: darkest };
      }
    }
    return null;
  }
}

function median(v: number[]): number {
  const s = [...v].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)]!;
}

export interface FlickerRecord extends Dip {
  /** Page time, ms. */
  at: number;
  frame: number;
  dtMs: number;
  context: Record<string, unknown>;
  /** A small picture of the last dark frame, and of the frame before the dip. */
  image?: string;
  before?: string;
}

const W = 96, H = 54;
const MAX_RECORDS = 50;

/** Reads the WebGL canvas right after it is drawn (same task, so the drawing buffer is still there). */
export class FlickerProbe {
  readonly records: FlickerRecord[] = [];
  private detector = new DipDetector();
  /** The last few frames' thumbnails, newest last, to show a dip once it has ended. */
  private ring: HTMLCanvasElement[] = [];
  private next = 0;

  constructor() {
    for (let i = 0; i < DIP_MAX_FRAMES + 2; i++) {
      const c = document.createElement('canvas');
      c.width = W; c.height = H;
      this.ring.push(c);
    }
  }

  sample(source: HTMLCanvasElement, frame: number, dtMs: number, context: () => Record<string, unknown>): FlickerRecord | null {
    const slot = this.ring[this.next % this.ring.length]!;
    this.next++;
    const g = slot.getContext('2d', { willReadFrequently: true });
    if (!g) return null;
    g.drawImage(source, 0, 0, W, H);
    const px = g.getImageData(0, 0, W, H).data;
    let sum = 0;
    for (let i = 0; i < px.length; i += 4) sum += 0.2126 * px[i]! + 0.7152 * px[i + 1]! + 0.0722 * px[i + 2]!;
    const lum = sum / (W * H * 255);
    const dip = this.detector.push(lum);
    if (!dip) return null;
    // the last dark frame, and the frame just before the dip
    const back = (n: number) => this.ring[(this.next - 1 - n + this.ring.length * 4) % this.ring.length]!;
    const rec: FlickerRecord = {
      ...dip, at: Math.round(performance.now()), frame, dtMs: Math.round(dtMs * 10) / 10, context: context(),
      image: back(1).toDataURL('image/png'), before: back(dip.frames + 1).toDataURL('image/png'),
    };
    this.records.push(rec);
    if (this.records.length > MAX_RECORDS) this.records.shift();
    return rec;
  }
}
